/**
 * Prep › Batches (#161, D20): what is on the shelf, how fresh it is, how
 * many serves are left, and making more.
 *
 * Making a batch is the one place bottles become batch. The form says what
 * the run will take before it takes it — the batch's build scaled to the
 * volume — and which of those the venue keeps no count of, so nobody is
 * surprised by a level that did not move.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  batchComponents,
  batchStatuses,
  inBottles,
  isOnMenu,
  type BatchStatus,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { venueZone } from "../format.js";
import { prepApi, type VenueBatches } from "./api.js";
import { HouseMadeEditor } from "./HouseMadeEditor.js";

/** What a batch is made to when nobody has said: a bottle's worth. */
const DEFAULT_RUN_ML = 700;

const litres = (ml: number): string => (ml >= 1000 ? `${+(ml / 1000).toFixed(2)} L` : `${Math.round(ml)} mL`);

function when(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleDateString("en-AU", { timeZone: venueZone(), weekday: "short", day: "numeric", month: "short" });
}

const FRESH_LABEL: Readonly<Record<BatchStatus["freshness"], string>> = {
  expired: "Past its date",
  expiring: "Use today",
  fresh: "Fresh",
  unknown: "",
};

export function Batches(props: { readonly venueId: string; /** A batch to arrive on, from a recipe's link. */ readonly open?: string | undefined }) {
  const app = useApp();
  const { token, library } = app;
  const [data, setData] = useState<VenueBatches | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [everyDrink, setEveryDrink] = useState(false);
  /** The batch whose "make" form is open, and the volume typed into it. */
  const [making, setMaking] = useState<string | null>(null);
  const [volume, setVolume] = useState("");
  const [saving, setSaving] = useState(false);
  /** The house-made ingredient whose recipe is open to edit; "" for a new one. */
  const [editing, setEditing] = useState<string | null>(props.open ?? null);
  // Arriving from a recipe: the batch's recipe is open, and in view.
  // The address drops the batch once the tab is open, so what was asked for
  // is kept from the first render; the scroll waits for the rows to lay out.
  const [arrival] = useState(props.open);
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    if (!arrival || arrived || data === null) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`batch-${arrival}`)?.scrollIntoView({ block: "start" });
      setArrived(true);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [arrival, arrived, data]);
  const canEditRecipe = app.me.can["ingredient:edit"];

  const load = useCallback(async () => {
    try {
      setData(await prepApi.batches(token, props.venueId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the batches");
    }
  }, [token, props.venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ingredients = useMemo(
    () => new Map(library.ingredients.map((i) => [i.ingredientId, i])),
    [library.ingredients],
  );

  const statuses = useMemo(() => {
    if (!data) return [];
    const onMenu = new Set(
      library.specs.filter((s) => isOnMenu(s, app.overrides.get(s.specId))).map((s) => s.specId),
    );
    return batchStatuses({
      ingredients: library.ingredients,
      recipes: library.specs,
      heldMlByIngredientId: new Map(Object.entries(data.heldMl)),
      runs: data.runs,
      // Tonight's list, or every drink in the library when asked.
      onMenuSpecIds: everyDrink ? undefined : onMenu,
    });
  }, [data, library.specs, app.overrides, everyDrink]);

  // Every house-made ingredient nobody pours and nobody holds: still worth
  // finding, to give it a recipe or put it in one.
  const idle = useMemo(() => {
    const shown = new Set(statuses.map((b) => b.batchId));
    return library.ingredients.filter((i) => i.build !== undefined && i.build !== null && !shown.has(i.ingredientId));
  }, [library.ingredients, statuses]);

  const open = (b: BatchStatus) => {
    setEditing(null);
    setMaking(b.batchId);
    setVolume(String(b.build?.yieldMl ?? DEFAULT_RUN_ML));
    setMsg(null);
    setError(null);
  };

  const make = async (b: BatchStatus) => {
    const ml = Number(volume);
    if (!Number.isFinite(ml) || ml <= 0) {
      setError("Enter how much was made, in millilitres");
      return;
    }
    setSaving(true);
    try {
      const { run, notCounted } = await prepApi.makeBatch(token, props.venueId, b.batchId, ml);
      setMaking(null);
      setMsg(
        `Made ${litres(run.volumeMl)} of ${b.name}.` +
          (run.components.length > notCounted.length
            ? ` Taken off the shelf: ${run.components
                .filter((part) => !notCounted.includes(part.ingredientId))
                .map((part) => `${litres(part.ml)} ${ingredients.get(part.ingredientId)?.name ?? part.ingredientId}`)
                .join(", ")}.`
            : "") +
          (notCounted.length > 0
            ? ` Not taken, because this venue keeps no count of them: ${notCounted.map((id) => ingredients.get(id)?.name ?? id).join(", ")}.`
            : ""),
      );
      await load();
      // The batch's own level may be new, and the dashboard reads stock.
      await app.refreshVenue();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "Your role does not allow making a batch at this venue."
          : err instanceof Error
            ? err.message
            : "Could not record the batch",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div className="label" style={{ lineHeight: 1.6, flex: "1 1 320px" }}>
          Making a batch takes what goes in it off the shelf and adds the batch. From then on a sale takes batch, not
          bottles, and the batch is counted in the stocktake.
        </div>
        <label className="row" style={{ gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={everyDrink} onChange={(e) => setEveryDrink(e.target.checked)} />
          Include batches for drinks off tonight&rsquo;s list
        </label>
        {canEditRecipe && editing !== "" && (
          <button className="btn dashed" onClick={() => { setEditing(""); setMaking(null); setMsg(null); }}>+ House-made ingredient</button>
        )}
      </div>
      {editing === "" && (
        <div className="table batch-table" style={{ marginBottom: 12 }}>
          <div className="batch-row">
            <HouseMadeEditor
              ingredient={undefined}
              onClose={() => setEditing(null)}
              onSaved={(ing) => { setEditing(null); setMsg(`${ing.name} added. Pour it from a recipe, or make some, and it is listed here.`); }}
            />
          </div>
        </div>
      )}

      {error && <div className="msg err" style={{ marginBottom: 10 }}>{error}</div>}
      {msg && <div className="msg ok" style={{ marginBottom: 10 }}>{msg}</div>}

      {data === null && !error && <div className="label">Reading the batches…</div>}
      {data !== null && statuses.length === 0 && (
        <div className="q-empty">
          No batches in use. A batch appears here once a recipe on the list pours it, or the venue holds some.
          A house-made ingredient is one with a build on its ingredient.
        </div>
      )}

      <div className="table batch-table">
        {statuses.map((b) => {
          const ingredient = ingredients.get(b.batchId);
          const parts = making === b.batchId && ingredient ? batchComponents(ingredient, Number(volume) || 0) : [];
          return (
            <div className="batch-row" key={b.batchId} id={`batch-${b.batchId}`} data-fresh={b.freshness}>
              <div className="batch-main">
                <span className="batch-name">{b.name}</span>
                <span className="note">
                  {b.uses.length > 0
                    ? b.uses.map((u) => `${u.name} ${+u.pourMl.toFixed(1)} mL`).join(" · ")
                    : "No drink on the list pours it"}
                </span>
              </div>
              <div className="batch-fact">
                <span className="label">On the shelf</span>
                <span className="num">{b.heldMl === null ? "Not counted yet" : litres(Math.max(0, b.heldMl))}</span>
              </div>
              <div className="batch-fact">
                <span className="label">Serves left</span>
                <span className="num">{b.servesLeft ?? "—"}</span>
              </div>
              <div className="batch-fact">
                <span className="label">Last made</span>
                <span className="num">
                  {b.lastRun ? `${when(b.lastRun.madeAt)} · ${litres(b.lastRun.volumeMl)}` : "Never here"}
                </span>
                {b.lastRun?.expiresAt && (
                  <span className="note batch-fresh">
                    {FRESH_LABEL[b.freshness] || "Keeps"} · until {when(b.lastRun.expiresAt)}
                  </span>
                )}
              </div>
              <div className="batch-act">
                {canEditRecipe && editing !== b.batchId && (
                  <button className="btn" onClick={() => { setEditing(b.batchId); setMaking(null); setMsg(null); }}>Recipe</button>
                )}
                {making === b.batchId ? (
                  <button className="btn" onClick={() => setMaking(null)} disabled={saving}>Cancel</button>
                ) : (
                  <button className="btn" onClick={() => open(b)}>Make</button>
                )}
              </div>

              {editing === b.batchId && (
                <HouseMadeEditor
                  ingredient={ingredient}
                  onClose={() => setEditing(null)}
                  onSaved={(ing) => { setEditing(null); setMsg(`${ing.name} saved and re-costed.`); }}
                />
              )}

              {making === b.batchId && (
                <div className="batch-make">
                  <label className="field" style={{ maxWidth: 220 }}>
                    <span>How much was made (mL)</span>
                    <input
                      className="num-input"
                      style={{ width: "100%" }}
                      type="number"
                      min={1}
                      step={50}
                      autoFocus
                      value={volume}
                      onChange={(e) => setVolume(e.target.value)}
                    />
                  </label>
                  <div className="batch-parts">
                    {!b.hasBuild && (
                      <div className="label" style={{ lineHeight: 1.6 }}>
                        No recipe on file for this batch, so making it adds it to the shelf and takes nothing off.
                      </div>
                    )}
                    {parts.map((part) => {
                      const ing = ingredients.get(part.ingredientId);
                      const split = inBottles(part.ml, ing?.bottleMl);
                      const counted = data !== null && part.ingredientId in data.heldMl;
                      return (
                        <div className="batch-part" key={part.ingredientId}>
                          <span className="num">{litres(part.ml)}</span>
                          <span>{ing?.name ?? part.ingredientId}</span>
                          <span className="note">
                            {split.bottles > 0
                              ? `${split.bottles} bottle${split.bottles === 1 ? "" : "s"} and ${split.remainderMl} mL`
                              : ""}
                            {!counted ? `${split.bottles > 0 ? " · " : ""}not counted here, so nothing is taken` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button className="btn primary" disabled={saving} onClick={() => void make(b)}>
                    {saving ? "Recording…" : `Record ${litres(Number(volume) || 0)} made`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {idle.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="label" style={{ marginBottom: 8 }}>House-made, but nothing on the list pours them and none is on the shelf</div>
          <div className="table batch-table">
            {idle.map((ing) => (
              <div className="batch-row" key={ing.ingredientId} id={`batch-${ing.ingredientId}`} data-fresh="unknown">
                <div className="batch-main">
                  <span className="batch-name">{ing.name}</span>
                  <span className="note">{ing.build?.lines.length ? `${ing.build.lines.length} lines` : "No recipe on file"}</span>
                </div>
                <div className="batch-act">
                  {canEditRecipe && editing !== ing.ingredientId && (
                    <button className="btn" onClick={() => { setEditing(ing.ingredientId); setMaking(null); setMsg(null); }}>Recipe</button>
                  )}
                </div>
                {editing === ing.ingredientId && (
                  <HouseMadeEditor
                    ingredient={ing}
                    onClose={() => setEditing(null)}
                    onSaved={(saved) => { setEditing(null); setMsg(`${saved.name} saved and re-costed.`); }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
